/**
 * View for the Dictate button shown in the navigation header.
 *
 * Three states: idle ("Dictate"), recording (hidden), completed ("Dictated" disabled).
 * @module features/voice-report/components/DictateButton/DictateButtonView
 */

import React, { memo } from "react";

// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: unavoidable primitive usage outside atoms (@/components/ui import(s): @/components/ui/button) —…
import { Button, ButtonText } from "@/components/ui/button";

import MicIcon from "@/features/voice-report/components/molecules/MicIcon";

/**
 * Props for DictateButtonView
 */
interface DictateButtonViewProps {
  /** Called when button is pressed */
  readonly onPress: () => void;
  /** Whether dictation has been completed (button disabled) */
  readonly isCompleted: boolean;
  /** Whether the button should be hidden (during recording/processing) */
  readonly isHidden: boolean;
}

/**
 * Dictate button for navigation header.
 * @param props - Component props
 * @param props.onPress - Press handler
 * @param props.isCompleted - Completed state
 * @param props.isHidden - Hidden state
 */
const DictateButtonView = ({
  onPress,
  isCompleted,
  isHidden,
}: DictateButtonViewProps) =>
  isHidden ? null : (
    <Button
      variant="outline"
      size="sm"
      onPress={onPress}
      isDisabled={isCompleted}
      className={`rounded-sm ${isCompleted ? "border-needs-semantic-success-300 opacity-70" : "border-accent-primary"}`}
    >
      <MicIcon color={isCompleted ? "#22c55e" : "#007aff"} />
      <ButtonText
        className={`font-medium ${isCompleted ? "text-status-success" : "text-accent-primary"}`}
      >
        {isCompleted ? "Dictated" : "Dictate"}
      </ButtonText>
    </Button>
  );

DictateButtonView.displayName = "DictateButtonView";

export default memo(DictateButtonView);
